import { get } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { Button } from '~/eds/src/components/Button/Button';
import { Checkbox } from '~/eds/src/components/Checkbox/Checkbox';
import { EmailInput } from '~/eds/src/components/EmailInput/EmailInput';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Panel } from '~/eds/src/components/Panel/Panel';
import { Panel_DEPRECATED } from '~/eds/src/components/Panel/Panel_DEPRECATED';
import { Select } from '~/eds/src/components/Select/Select';
import { Text } from '~/eds/src/components/Text/Text';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { INPUT_DEBOUNCE_MS } from '~/eds/src/constants/duration';
import { useDebounce } from '~/eds/src/hooks/useDebounce';
import { Option } from '~/eds/src/types/Option';
import { testIsEmailValid } from '~/eds/src/utils/testIsEmailValid';
import { RoleType } from '~/enums/Roles';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { api } from '~/redux/api/createApi';
import {
  CreateUserResponse,
  RoleAttributes,
  UserAttributes,
} from '~/redux/api/methods/roles/roles';
import { ParsedType } from '~/redux/api/transformers/utils';
import { Nullable } from '~/types/Nullable';
import { PilotId } from '~/types/PilotId';
import { ERROR, SUCCESS } from '~/types/toast.types';
import { testIsSuperAdmin } from '~/utils/user';

import DepartmentSelect from '../Shared/DepartmentSelect/DepartmentSelect';
import { showToast } from '../Shared/EcToast/showToast';

type Props = {
  visible: boolean;
  onHide: () => void;
  user: Nullable<ParsedType<UserAttributes>>;
  clientId?: PilotId;
  isWorkdayUser?: boolean;
};

export const UserFormPanel = ({
  visible,
  onHide,
  user,
  clientId,
  isWorkdayUser,
}: Props) => {
  const intl = useIntl();
  const [
    getRoles,
    { isFetching: isFetchingRoles, data: roles, isLoading: isLoadingRoles },
  ] = api.endpoints.getRolesSimple.useLazyQuery();
  const currentUser = useCurrentUser();

  const isRolodexDataSourceEnabled = useFlag(FlagType.RolodexAsUserDataSource);
  const enableModalConsolidation = useFlag(FlagType.ModalConsolidation);

  const [createUser, createUserResult] = api.endpoints.createUser.useMutation();
  const {
    isLoading: isLoadingCreate,
    isError: isErrorCreate,
    isSuccess: isSuccessCreate,
    data: dataCreate,
    error: errorCreate,
  } = createUserResult;
  const [updateUser, updateUserResult] = api.endpoints.updateUser.useMutation();
  const {
    isLoading: isUpdating,
    isError: isErrorUpdate,
    isSuccess: isSuccessUpdate,
    error: errorUpdate,
    data: dataUpdate,
  } = updateUserResult;
  const [updateWorkdayUser] = api.endpoints.updateWorkdayUser.useMutation();
  const [sendInvitationEmail] = api.endpoints.sendInvitationEmail.useMutation();

  const [firstName, setFirstName] = useState<Nullable<string>>('');
  const [lastName, setLastName] = useState<Nullable<string>>('');
  const [email, setEmail] = useState<Nullable<string>>('');
  const [jobTitle, setJobTitle] = useState<Nullable<string>>('');
  const [departments, setDepartments] = useState<Nullable<number[]>>([]);
  const [role, setRole] = useState<Nullable<string>>('');
  const [emailInstructions, setEmailInstructions] =
    useState<Nullable<boolean>>(true);
  const roleOptions = useMemo(
    () => roles?.results.map(roleAsSelectOption) || [],
    [roles?.results],
  );

  const groupedItems: Array<Option<RoleType, unknown>> = useMemo(
    () =>
      Object.values(
        roleOptions.reduce(
          (acc: Record<RoleType, Option>, item) => {
            const { description, label, value } = item;
            if (!acc[description]) {
              acc[description] = {
                label: description,
                options: [],
                value: `${description}-${value}`,
              };
            }
            acc[description].options!.push({ label, value });
            return acc;
          },
          {} as Record<RoleType, Option>,
        ),
      ) as Array<Option<RoleType, unknown>>,
    [roleOptions],
  );

  const reset = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setJobTitle('');
    setDepartments([]);
    setEmailInstructions(true);
    setRole(null);
  };

  const handleHide = () => {
    reset();
    onHide();
  };
  const handleSubmit = () => {
    if (user && isWorkdayUser) {
      updateWorkdayUser({
        userId: user.id,
        body: {
          roleUuid: role!,
          pilotDepartmentIds: departments || [],
        },
      })
        .unwrap()
        .then(() => handleSuccess(undefined, successMessage))
        .catch((err: Error) => showErrorMessage(err.message));
    } else if (user) {
      updateUser({
        userId: user.id,
        body: {
          first_name: firstName!,
          last_name: lastName!,
          email: email!,
          job_title: jobTitle,
          departments: departments || [],
          role_uuid: role!,
          client: Number(clientId) ?? currentUser?.client,
        },
        enableRolodex: isWorkdayUser || isRolodexDataSourceEnabled,
      });
    } else {
      createUser({
        body: {
          first_name: firstName!,
          last_name: lastName!,
          email: email!,
          job_title: jobTitle,
          departments: departments || [],
          role_uuid: role!,
          client: Number(clientId) ?? currentUser?.client,
        },
        enableRolodex: isWorkdayUser || isRolodexDataSourceEnabled,
      });
    }
  };

  useEffect(() => {
    if (user) {
      setFirstName(user.firstName);
      setLastName(user.lastName);
      setEmail(user.email);
      setJobTitle(user.jobTitle);
      setDepartments(
        (user.departments || []).map((department) => department.departmentId),
      );
      setRole(user.roleId);
    }
  }, [user]);

  const handleSearchRole = useDebounce((search: string) => {
    getRoles({
      page: 1,
      pageSize: 100,
      name: search,
      client_id: clientId,
    });
  }, INPUT_DEBOUNCE_MS);

  useEffect(() => {
    getRoles({
      page: 1,
      pageSize: 100,
      client_id: clientId,
    });
  }, []);

  const handleSuccess = (
    successData?: CreateUserResponse,
    successMessage?: string,
  ) => {
    showToast(SUCCESS, successMessage);
    const isEmailUpdated = user?.email !== email;
    if (
      (emailInstructions && !user) ||
      (user?.id && isEmailUpdated && user?.status === 'Pending')
    ) {
      sendInvitationEmail({
        userId: get(successData, 'data.id', ''),
        enableRolodex: isWorkdayUser || isRolodexDataSourceEnabled,
      });
    }
    handleHide();
  };

  const successMessage = user
    ? intl.formatMessage(
        {
          id: 'UserFormPanel.userUpdatedSuccess',
          defaultMessage: 'User {firstName} updated successfully!',
          description:
            'Success message when user is updated. Example: firstName = "John"',
        },
        { firstName },
      )
    : intl.formatMessage(
        {
          id: 'UserFormPanel.userCreatedSuccess',
          defaultMessage: 'User {firstName} created successfully!',
          description:
            'Success message when user is created. Example: firstName = "John"',
        },
        { firstName },
      );

  useEffect(() => {
    if (isSuccessCreate) {
      handleSuccess(dataCreate, successMessage);
    }
  }, [isSuccessCreate, dataCreate]);

  useEffect(() => {
    if (isSuccessUpdate) {
      handleSuccess(dataUpdate, successMessage);
    }
  }, [isSuccessUpdate, dataUpdate]);

  const showErrorMessage = (
    error: unknown,
    message = intl.formatMessage({
      id: 'UserFormPanel.defaultErrorMessage',
      defaultMessage: 'Something went wrong. Please try again later.',
      description: 'Default error message for user operations',
    }),
  ) => {
    const errorMessage = get(
      error,
      'response.data.non_field_errors[0]',
      get(error, 'response.data.email[0]', message),
    );
    showToast(ERROR, errorMessage);
  };

  useEffect(() => {
    if (isErrorCreate) {
      showErrorMessage(errorCreate);
    }
  }, [isErrorCreate, errorCreate]);

  useEffect(() => {
    if (isErrorUpdate) {
      showErrorMessage(errorUpdate);
    }
  }, [isErrorUpdate, errorUpdate]);

  const isSubmitDisabled =
    !firstName || !lastName || !testIsEmailValid(email) || !role;
  const title = user
    ? intl.formatMessage(
        {
          id: 'UserFormPanel.editUserTitle',
          defaultMessage: 'Edit "{firstName} {lastName}"',
          description:
            'Title for editing an existing user. Example: firstName = "John", lastName = "Smith"',
        },
        { firstName, lastName },
      )
    : intl.formatMessage({
        id: 'UserFormPanel.newUserTitle',
        defaultMessage: 'New User',
        description: 'Title for creating a new user',
      });
  const subtitle = user
    ? intl.formatMessage({
        id: 'UserFormPanel.editUserSubtitle',
        defaultMessage: 'Edit user profile and permissions.',
        description: 'Subtitle for editing user form',
      })
    : intl.formatMessage({
        id: 'UserFormPanel.newUserSubtitle',
        defaultMessage: 'Create a new user in your organization.',
        description: 'Subtitle for new user form',
      });

  const optionalLabel = intl.formatMessage({
    id: 'UserFormPanel.optionalLabel',
    defaultMessage: '(Optional)',
    description: 'Label indicating field is optional',
  });

  const children = (
    <>
      <Layout direction="column" spacing="2">
        <Text>{subtitle}</Text>

        <Text variant="section">
          {intl.formatMessage({
            id: 'UserFormPanel.generalInformationSection',
            defaultMessage: 'General Information',
            description: 'Section header for general information',
          })}
        </Text>

        <Layout direction="column" spacing="1">
          <Text variant="body-bold">
            {intl.formatMessage({
              id: 'UserFormPanel.firstNameLabel',
              defaultMessage: 'First name',
              description: 'Label for first name field',
            })}
          </Text>
          <TextInput
            disabled={isWorkdayUser}
            placeholder={intl.formatMessage({
              id: 'UserFormPanel.firstNamePlaceholder',
              defaultMessage: 'First name',
              description: 'Placeholder for first name field',
            })}
            name="firstName"
            value={firstName}
            onChange={setFirstName}
          ></TextInput>
        </Layout>

        <Layout direction="column" spacing="1">
          <Text variant="body-bold">
            {intl.formatMessage({
              id: 'UserFormPanel.lastNameLabel',
              defaultMessage: 'Last name',
              description: 'Label for last name field',
            })}
          </Text>
          <TextInput
            disabled={isWorkdayUser}
            placeholder={intl.formatMessage({
              id: 'UserFormPanel.lastNamePlaceholder',
              defaultMessage: 'Last name',
              description: 'Placeholder for last name field',
            })}
            name="lastName"
            value={lastName}
            onChange={setLastName}
          ></TextInput>
        </Layout>

        <Layout direction="column" spacing="1">
          <Text variant="body-bold">
            {intl.formatMessage({
              id: 'UserFormPanel.emailLabel',
              defaultMessage: 'Email',
              description: 'Label for email field',
            })}
          </Text>
          <EmailInput
            disabled={isWorkdayUser}
            placeholder={intl.formatMessage({
              id: 'UserFormPanel.emailPlaceholder',
              defaultMessage: 'Email',
              description: 'Placeholder for email field',
            })}
            name="email"
            value={email}
            onChange={setEmail}
          ></EmailInput>
          {!user && (
            <Checkbox
              name="sign-in-instructions'"
              value={emailInstructions}
              onChange={setEmailInstructions}
              option={{
                label: intl.formatMessage({
                  id: 'UserFormPanel.emailInstructionsCheckbox',
                  defaultMessage: 'Email sign-in instructions',
                  description:
                    'Checkbox label for emailing sign-in instructions',
                }),
                value: 'sign-in-instructions',
              }}
            />
          )}
        </Layout>

        <Layout direction="column" spacing="1">
          <Text variant="body-bold">
            {intl.formatMessage({
              id: 'UserFormPanel.jobTitleLabel',
              defaultMessage: 'Job title',
              description: 'Label for job title field',
            })}{' '}
            <Text variant="tiny" color="text.secondary">
              {optionalLabel}
            </Text>
          </Text>
          <TextInput
            disabled={isWorkdayUser}
            placeholder={intl.formatMessage({
              id: 'UserFormPanel.jobTitlePlaceholder',
              defaultMessage: 'Job title',
              description: 'Placeholder for job title field',
            })}
            name="jobTitle"
            value={jobTitle}
            onChange={setJobTitle}
          ></TextInput>
        </Layout>

        <Layout direction="column" spacing="1">
          <Text variant="body-bold">
            {intl.formatMessage({
              id: 'UserFormPanel.departmentLabel',
              defaultMessage: 'Department',
              description: 'Label for department field',
            })}{' '}
            <Text variant="tiny" color="text.secondary">
              {optionalLabel}
            </Text>
          </Text>
          <Text color="text.secondary">
            {intl.formatMessage({
              id: 'UserFormPanel.departmentDescription',
              defaultMessage:
                'Select a department for organizing purposes only. Use groups to grant access to documents and workflows.',
              description: 'Description for department field',
            })}
          </Text>
          <DepartmentSelect
            value={departments}
            onChange={setDepartments}
            isMulti={true}
            width="fullWidth"
            clientId={testIsSuperAdmin(currentUser) ? clientId : undefined}
          />
        </Layout>
      </Layout>
      <Layout direction="column" spacing="2" mt="6">
        <Text variant="section">
          {intl.formatMessage({
            id: 'UserFormPanel.permissionsSection',
            defaultMessage: 'Permissions',
            description: 'Section header for permissions',
          })}
        </Text>

        <Layout
          direction="column"
          spacing="1"
          aria-labelledby="role-field-label"
        >
          <Text variant="body-bold" id="role-field-label">
            {intl.formatMessage({
              id: 'UserFormPanel.roleLabel',
              defaultMessage: 'Role',
              description: 'Label for role field',
            })}
          </Text>
          <Text color="text.secondary">
            {intl.formatMessage({
              id: 'UserFormPanel.roleDescription',
              defaultMessage:
                'Assign a role to the user to grant permissions in Evisort.',
              description: 'Description for role field',
            })}
          </Text>
          <Select
            placeholder={intl.formatMessage({
              id: 'UserFormPanel.rolePlaceholder',
              defaultMessage: 'Select a role',
              description: 'Placeholder for role selection',
            })}
            isLoading={isFetchingRoles}
            options={groupedItems}
            menuPlacement="top"
            name="role"
            value={role}
            onChange={setRole}
            onSearch={handleSearchRole}
            isMulti={false}
            isSearchable={true}
          />
        </Layout>
      </Layout>
    </>
  );

  if (enableModalConsolidation && visible) {
    return (
      <Panel
        title={title}
        loadingContent={{
          isLoading: isLoadingRoles,
        }}
        enableBackdrop
        mode="floating"
        width="560px"
        hidden={{ onHide: handleHide }}
        children={children}
        footer={{
          actions: [
            {
              mode: 'chip',
              text: intl.formatMessage({
                id: 'UserFormPanel.cancelButton',
                defaultMessage: 'Cancel',
                description: 'Cancel button text',
              }),
              onClick: handleHide,
            },
            {
              mode: 'chip',
              level: 'primary',
              text: intl.formatMessage({
                id: 'UserFormPanel.saveButton',
                defaultMessage: 'Save',
                description: 'Save button text',
              }),
              onClick: handleSubmit,
              isLoading: isLoadingCreate || isUpdating,
              disabled: isSubmitDisabled,
            },
          ],
        }}
      />
    );
  }

  return (
    <Panel_DEPRECATED
      width="m"
      isVisible={visible}
      title={title}
      onHide={handleHide}
      isPadded={true}
      mode="blocking"
      isLoading={isLoadingRoles}
      footer={
        <Layout spacing="2" justify="flex-end" borderTop="border" p="3">
          <Button
            variant="tertiary"
            text={intl.formatMessage({
              id: 'UserFormPanel.cancelButton',
              defaultMessage: 'Cancel',
              description: 'Cancel button text',
            })}
            onClick={handleHide}
          />
          <Button
            variant="primary"
            text={intl.formatMessage({
              id: 'UserFormPanel.saveButton',
              defaultMessage: 'Save',
              description: 'Save button text',
            })}
            onClick={handleSubmit}
            isLoading={isLoadingCreate || isUpdating}
            disabled={isSubmitDisabled}
          />
        </Layout>
      }
    >
      {children}
    </Panel_DEPRECATED>
  );
};

const roleAsSelectOption = (role: ParsedType<RoleAttributes>) => ({
  label: role.name,
  value: role.id,
  description: role.roleType,
});
